import React, { useMemo, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { ActivityIcon } from 'lucide-react';
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { useTimelineContext } from '../context/TimelineContext';
import { TimelineEventCard } from './TimelineEventCard';
import { TimelineEmptyState } from './TimelineEmptyState';

/**
 * Adds events to the timeline events object based on filter
 */
const addEventsToTimeline = (
  events: { date: string }[],
  type: string,
  allEvents: { [key: string]: Record<string, unknown>[] }
) => {
  events.forEach((event) => {
    const dateKey = event.date;
    if (!allEvents[dateKey]) allEvents[dateKey] = [];
    allEvents[dateKey].push({ ...event, type });
  });
};

export const TimelineEventList: React.FC = () => {
  const { timelineData, filters, config, isLoading } = useTimelineContext();
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Organize events by date based on active filters
  const { sortedDates, allEvents } = useMemo(() => {
    const allEvents: { [key: string]: Record<string, unknown>[] } = {};

    // Check if any filters are active
    const hasActiveFilters = Object.values(filters).some(Boolean);

    // If no filters are active, show everything; otherwise show only selected types
    if (config.showStatusHistory && (!hasActiveFilters || filters.status)) {
      addEventsToTimeline(timelineData.statusChanges, "status", allEvents);
    }

    if (config.showSchedule && (!hasActiveFilters || filters.schedule)) {
      addEventsToTimeline(timelineData.scheduleDates, "schedule", allEvents);
    }

    if (config.showPlanning && (!hasActiveFilters || filters.planning)) {
      addEventsToTimeline(timelineData.planningDates, "planning", allEvents);
    }

    if (config.showHistory && (!hasActiveFilters || filters.history)) {
      addEventsToTimeline(timelineData.historyDates, "history", allEvents);
    }

    if (!hasActiveFilters || filters.reminders) {
      addEventsToTimeline(timelineData.reminderDates, "reminder", allEvents);
    }

    // Display all events in descending order (newest first)
    const sortedDates = Object.keys(allEvents).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    return { sortedDates, allEvents };
  }, [timelineData, filters, config]);

  // Find the closest date to today and scroll to it
  useEffect(() => {
    if (sortedDates.length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find the date closest to today
    let closestDateKey = sortedDates[0];
    let minDiff = Math.abs(parseISO(sortedDates[0]).getTime() - today.getTime());

    sortedDates.forEach((dateKey) => {
      const diff = Math.abs(parseISO(dateKey).getTime() - today.getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closestDateKey = dateKey;
      }
    });

    // Scroll to the closest date
    const targetElement = dateRefs.current[closestDateKey];
    if (targetElement) {
      // Use a small delay to ensure the DOM is fully rendered
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [sortedDates]);

  if (isLoading) {
    return (
      <div className="flex-1 overflow-hidden p-0 min-h-0">
        <ScrollArea className="h-full">
          <div className="flex h-full flex-col first:pt-0">
            <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/30 px-4 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-primary">
                <ActivityIcon className="h-6 w-6 animate-spin" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Loading timeline</p>
                <p className="text-xs text-muted-foreground">Fetching {config.showStatusHistory ? 'status history, ' : ''}resources, {config.showPlanning ? 'planning, ' : ''}{config.showHistory ? 'history, ' : ''}and reminders…</p>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  }

  if (sortedDates.length === 0) {
    return (
      <div className="flex-1 overflow-hidden p-0 min-h-0">
        <ScrollArea className="h-full">
          <div className="flex h-full flex-col first:pt-0">
            <TimelineEmptyState />
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden p-0 min-h-0">
      <ScrollArea className="h-full [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div className="flex h-full flex-col first:pt-0">
          <div className="space-y-2 px-6">
            <div className="h-2" />
            {sortedDates.map((dateKey, dateIdx) => {
              const events = allEvents[dateKey];
              const date = parseISO(dateKey);

              return (
                <div
                  key={dateKey}
                  ref={(el) => {
                    dateRefs.current[dateKey] = el;
                  }}
                >
                  {dateIdx > 0 && <div className="mb-8" />}

                  <div className="mb-3">
                    <p className="text-sm text-muted-foreground">{format(date, "EEEE, MMMM d, yyyy")}</p>
                  </div>

                  <div className="space-y-2 bg-card/50 dark:bg-card/40 overflow-x-hidden">
                    {events.map((event: Record<string, unknown>, idx: number) => (
                      <TimelineEventCard key={`${event.type}-${idx}`} event={event} type={event.type as string} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
